import type { Agent } from '../../agent';
import { UserError } from '../../errors';
import type { ProcessedResponse } from '../../runner/types';
import type { FunctionTool, Tool } from '../../tool';
import {
  FUNCTION_TOOL_NAMESPACE,
  FUNCTION_TOOL_NAMESPACE_DESCRIPTION,
  getFunctionToolQualifiedName,
  resolveFunctionToolCallName,
} from '../../tool-identity';
import * as protocol from '../../types/protocol';
import { isSandboxAgent } from './agent-keys';

const SERIALIZED_EXECUTION_TOOL_PLACEHOLDER = Symbol(
  'serializedExecutionToolPlaceholder',
);

export function processedResponseRequiresExecutionToolRehydration(
  processedResponse: ProcessedResponse<any> | undefined,
): boolean {
  if (!processedResponse) {
    return false;
  }

  return (processedResponse.functions ?? []).some((item) =>
    isSerializedExecutionToolPlaceholder(item.tool),
  );
}

function isSerializedExecutionToolPlaceholder(tool: unknown): boolean {
  return Boolean(
    tool &&
      typeof tool === 'object' &&
      (tool as { [SERIALIZED_EXECUTION_TOOL_PLACEHOLDER]?: unknown })[
        SERIALIZED_EXECUTION_TOOL_PLACEHOLDER
      ] === true,
  );
}

export function getSerializedFunctionToolPlaceholder<TContext>(args: {
  agent: Agent<any, any>;
  baseAgentTools: Tool<any>[];
  serializedTool: unknown;
  toolCall: protocol.FunctionCallItem;
  toolIdentity: string;
  allowSerializedExecutionToolPlaceholder: boolean;
}): FunctionTool<TContext> | undefined {
  const {
    agent,
    baseAgentTools,
    serializedTool,
    toolCall,
    toolIdentity,
    allowSerializedExecutionToolPlaceholder,
  } = args;
  if (
    !canUseSerializedExecutionToolPlaceholder({
      agent,
      allowSerializedExecutionToolPlaceholder,
      configuredToolExists: hasConfiguredFunctionTool(
        baseAgentTools,
        toolCall,
        toolIdentity,
      ),
      serializedTool,
      type: 'function',
    })
  ) {
    return undefined;
  }

  const serialized = serializedTool as Partial<FunctionTool<TContext>>;
  const name = getSerializedToolName(serializedTool, toolCall.name);
  const placeholder = {
    ...serialized,
    type: 'function' as const,
    name,
    description:
      typeof serialized.description === 'string'
        ? serialized.description
        : `Serialized execution-time tool ${name}.`,
    parameters:
      serialized.parameters &&
      typeof serialized.parameters === 'object' &&
      !Array.isArray(serialized.parameters)
        ? serialized.parameters
        : {
            type: 'object',
            properties: {},
            required: [],
            additionalProperties: false,
          },
    strict: typeof serialized.strict === 'boolean' ? serialized.strict : true,
    invoke: async () => {
      throw createSerializedExecutionToolError('Function', toolIdentity);
    },
    needsApproval: async () => false,
    isEnabled: async () => true,
  } satisfies FunctionTool<TContext>;

  if (
    typeof toolCall.namespace === 'string' &&
    toolCall.namespace.length > 0 &&
    toolCall.namespace !== name
  ) {
    Object.assign(placeholder, {
      [FUNCTION_TOOL_NAMESPACE]: toolCall.namespace,
      [FUNCTION_TOOL_NAMESPACE_DESCRIPTION]: toolCall.namespace,
    });
  }

  return markSerializedExecutionToolPlaceholder(placeholder);
}

function canUseSerializedExecutionToolPlaceholder(args: {
  agent: Agent<any, any>;
  allowSerializedExecutionToolPlaceholder: boolean;
  configuredToolExists: boolean;
  serializedTool: unknown;
  type: Tool['type'];
}): boolean {
  const {
    agent,
    allowSerializedExecutionToolPlaceholder,
    configuredToolExists,
    serializedTool,
    type,
  } = args;
  return (
    allowSerializedExecutionToolPlaceholder &&
    isSandboxAgent(agent) &&
    !configuredToolExists &&
    Boolean(serializedTool) &&
    typeof serializedTool === 'object' &&
    (serializedTool as { type?: unknown }).type === type
  );
}

function hasConfiguredFunctionTool(
  tools: Tool<any>[],
  toolCall: protocol.FunctionCallItem,
  toolIdentity: string,
): boolean {
  const configuredFunctionTools = new Map(
    tools
      .filter((tool) => tool.type === 'function')
      .map((tool) => [getFunctionToolQualifiedName(tool) ?? tool.name, tool]),
  );
  const configuredIdentity =
    resolveFunctionToolCallName(toolCall, configuredFunctionTools) ??
    toolIdentity;
  return configuredFunctionTools.has(configuredIdentity);
}

function getSerializedToolName(
  serializedTool: unknown,
  fallbackName: string,
): string {
  const name =
    serializedTool &&
    typeof serializedTool === 'object' &&
    typeof (serializedTool as { name?: unknown }).name === 'string'
      ? (serializedTool as { name: string }).name
      : fallbackName;
  return name;
}

function createSerializedExecutionToolError(
  type: string,
  name: string,
): UserError {
  return new UserError(
    `${type} tool ${name} was restored from serialized execution-time metadata without an executable handler. Resume the RunState through Runner.run() with the matching execution-time tool configuration before executing it.`,
  );
}

function markSerializedExecutionToolPlaceholder<T extends object>(tool: T): T {
  Object.defineProperty(tool, SERIALIZED_EXECUTION_TOOL_PLACEHOLDER, {
    value: true,
    enumerable: false,
  });
  return tool;
}
